/**
 * Notification Settings Service
 * Mock service using localStorage until backend API is ready
 *
 * This service manages user notification preferences:
 * - Global enable/disable
 * - Toast popup settings
 * - Sound settings
 * - Per-category notification settings
 */

import { getUserSettingsByPage, upsertUserSetting } from '@/services/api/userSettingsApi';
import storage from '@/services/storage';

const STORAGE_KEY = 'wiseppc_notification_preferences';
const NOTIFICATIONS_CACHE_KEY = 'wiseppc_notifications_cache';
const MAX_CACHED_NOTIFICATIONS = 100;
// The cache mirrors recently-received notifications; entries older than this are stale and
// get reclaimed by the storage TTL sweep. Each save slides the window forward.
const NOTIFICATIONS_CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days
const SETTINGS_PAGE = 'notifications';
const SETTINGS_KEY = 'drawer-preferences';

const mergeWithDefaults = (raw) => {
  if (!raw || typeof raw !== 'object') return { ...defaultPreferences };
  return {
    ...defaultPreferences,
    ...raw,
    types: {
      ...defaultPreferences.types,
      ...(raw.types || {}),
    },
  };
};

/**
 * Default notification preferences
 */
export const defaultPreferences = {
  enabled: true,
  showToasts: true,
  sound: false,
  types: {
    sync: { enabled: true, toast: true },
    reports: { enabled: true, toast: true },
    campaigns: { enabled: true, toast: true },
    system: { enabled: true, toast: true },
    amazon_account: { enabled: true, toast: false },
  },
};

/**
 * Notification type category mapping
 * Maps backend notification types to frontend categories
 */
export const notificationTypeMapping = {
  // Sync notifications
  sync_started: { category: 'sync', icon: 'Refresh', defaultToast: false },
  sync_progress: { category: 'sync', icon: 'Refresh', defaultToast: false },
  sync_completed: { category: 'sync', icon: 'CheckCircle', defaultToast: true },
  sync_failed: { category: 'sync', icon: 'XCircle', defaultToast: true },

  // Report notifications
  report_ready: { category: 'reports', icon: 'FileText', defaultToast: true },
  report_failed: { category: 'reports', icon: 'FileX', defaultToast: true },

  // Campaign notifications
  campaign_status_changed: { category: 'campaigns', icon: 'Megaphone', defaultToast: false },
  budget_alert: { category: 'campaigns', icon: 'AlertTriangle', defaultToast: true },
  bid_adjusted: { category: 'campaigns', icon: 'DollarSign', defaultToast: false },
  rule_triggered: { category: 'campaigns', icon: 'Zap', defaultToast: true },

  // System notifications
  system_alert: { category: 'system', icon: 'Info', defaultToast: true },
  maintenance: { category: 'system', icon: 'Tool', defaultToast: true },
  feature_announcement: { category: 'system', icon: 'Sparkles', defaultToast: false },

  // Amazon account notifications (connect / reconnect / archive / revoked).
  // Single type; the specific event is in data.event. Routed to the User tab via
  // the amazon_account source below, not by category. Its own category (not
  // 'system') gives users a dedicated Enabled toggle, so silencing generic
  // system notifications does not drop actionable account alerts (e.g. revoked).
  // Icon is chosen per data.event in NotificationItem.vue.
  amazon_account: { category: 'amazon_account', icon: 'CheckCircle', defaultToast: false },

  // Entity update notifications (delivered over WebSocket). These are noisy by
  // nature (they echo every mutation), so defaultToast is false — the tab split
  // (see getNotificationTab) and the inline diff carry the signal instead.
  campaign_updated: { category: 'campaigns', icon: 'Megaphone', defaultToast: false },
  campaign_created: { category: 'campaigns', icon: 'Megaphone', defaultToast: false },
  adgroup_updated: { category: 'campaigns', icon: 'Layers', defaultToast: false },
  target_updated: { category: 'campaigns', icon: 'Target', defaultToast: false },
  ad_updated: { category: 'campaigns', icon: 'Megaphone', defaultToast: false },
  portfolio_updated: { category: 'campaigns', icon: 'Briefcase', defaultToast: false },
  portfolio_created: { category: 'campaigns', icon: 'Briefcase', defaultToast: false },
  negative_target_created: { category: 'campaigns', icon: 'Target', defaultToast: false },
  data_changed: { category: 'campaigns', icon: 'RefreshCw', defaultToast: false },
};

/**
 * Message sources (data.source) considered user-initiated.
 * Everything else (rule engine, sync, automation, platform) is treated as system.
 */
export const USER_SOURCES = ['api_mutation', 'amazon_account'];

/**
 * Resolve which panel tab a notification belongs to, based on who triggered it.
 * @param {Object} notification - Normalized notification ({ data: { source } })
 * @returns {'user'|'system'}
 */
export function getNotificationTab(notification) {
  return USER_SOURCES.includes(notification?.data?.source) ? 'user' : 'system';
}

/**
 * Get notification category for a type
 * @param {string} type - Notification type
 * @returns {string} Category name
 */
export function getNotificationCategory(type) {
  return notificationTypeMapping[type]?.category || 'system';
}

/**
 * Get notification icon for a type
 * @param {string} type - Notification type
 * @returns {string} Icon name
 */
export function getNotificationIcon(type) {
  return notificationTypeMapping[type]?.icon || 'Info';
}

/**
 * Notification Settings Service
 */
export const notificationSettingsService = {
  /**
   * Get user preferences from localStorage
   * @returns {Object} User preferences merged with defaults
   */
  getPreferences() {
    try {
      const stored = storage.get(STORAGE_KEY);
      if (stored) {
        // Merge with defaults to ensure all fields exist
        return {
          ...defaultPreferences,
          ...stored,
          types: {
            ...defaultPreferences.types,
            ...(stored.types || {}),
          },
        };
      }
      return { ...defaultPreferences };
    } catch (error) {
      console.error('[NotificationSettings] Error reading preferences:', error);
      return { ...defaultPreferences };
    }
  },

  /**
   * Save user preferences to localStorage
   * @param {Object} preferences - Preferences to save
   * @returns {Promise<Object>} Saved preferences (simulates async API)
   */
  async savePreferences(preferences) {
    try {
      const merged = {
        ...defaultPreferences,
        ...preferences,
        types: {
          ...defaultPreferences.types,
          ...(preferences.types || {}),
        },
      };
      storage.set(STORAGE_KEY, merged);
      return merged;
    } catch (error) {
      console.error('[NotificationSettings] Error saving preferences:', error);
      throw error;
    }
  },

  /**
   * Reset preferences to defaults
   * @returns {Promise<Object>} Default preferences
   */
  async resetPreferences() {
    try {
      storage.remove(STORAGE_KEY);
      return { ...defaultPreferences };
    } catch (error) {
      console.error('[NotificationSettings] Error resetting preferences:', error);
      throw error;
    }
  },

  /**
   * Check if a notification type should show a toast
   * @param {string} type - Notification type
   * @param {Object} preferences - Current preferences
   * @returns {boolean}
   */
  shouldShowToast(type, preferences) {
    if (!preferences.enabled || !preferences.showToasts) {
      return false;
    }

    const category = getNotificationCategory(type);
    const categoryPrefs = preferences.types[category];

    if (!categoryPrefs?.enabled || !categoryPrefs?.toast) {
      return false;
    }

    return true;
  },

  /**
   * Check if a notification type is enabled
   * @param {string} type - Notification type
   * @param {Object} preferences - Current preferences
   * @returns {boolean}
   */
  isNotificationEnabled(type, preferences) {
    if (!preferences.enabled) {
      return false;
    }

    const category = getNotificationCategory(type);
    return preferences.types[category]?.enabled ?? true;
  },

  /**
   * Fetch settings from server (with localStorage fallback).
   * On a fresh device the server is authoritative; if it errors,
   * fall back to whatever was last cached locally so the UI still loads.
   * @returns {Promise<Object>} User preferences
   */
  async fetchSettings() {
    try {
      const res = await getUserSettingsByPage(SETTINGS_PAGE, { key: SETTINGS_KEY });
      const items = res?.data?.items || [];
      if (items.length > 0) {
        const merged = mergeWithDefaults(items[0].value);
        storage.set(STORAGE_KEY, merged);
        return merged;
      }
    } catch (error) {
      // During an auth transition (login / logout / registration) the axios
      // interceptor intentionally aborts non-auth requests (ERR_AUTH_LOADING).
      // That's expected — fall back to localStorage silently. Only log genuine
      // server failures.
      if (error?.code !== 'ERR_AUTH_LOADING') {
        console.warn('[NotificationSettings] Server fetch failed, using localStorage:', error);
      }
    }
    return this.getPreferences();
  },

  /**
   * Update settings on server (and mirror to localStorage as fallback).
   * @param {Object} preferences - Preferences to update
   * @returns {Promise<Object>} Updated preferences
   */
  async updateSettings(preferences) {
    const merged = await this.savePreferences(preferences);
    try {
      await upsertUserSetting({
        page_name: SETTINGS_PAGE,
        key: SETTINGS_KEY,
        value: merged,
        is_personal: true,
      });
    } catch (error) {
      console.warn('[NotificationSettings] Server update failed (kept in localStorage):', error);
    }
    return merged;
  },
};

/**
 * Notification Cache Service
 * Manages cached notifications in localStorage
 */
export const notificationCacheService = {
  /**
   * Get cached notifications
   * @returns {Array} Cached notifications
   */
  getNotifications() {
    try {
      const stored = storage.get(NOTIFICATIONS_CACHE_KEY);
      return stored || [];
    } catch (error) {
      console.error('[NotificationCache] Error reading cache:', error);
      return [];
    }
  },

  /**
   * Save notifications to cache
   * @param {Array} notifications - Notifications to cache
   */
  saveNotifications(notifications) {
    try {
      // Keep only the most recent notifications
      const toCache = notifications.slice(0, MAX_CACHED_NOTIFICATIONS);
      storage.set(NOTIFICATIONS_CACHE_KEY, toCache, { ttl: NOTIFICATIONS_CACHE_TTL });
    } catch (error) {
      console.error('[NotificationCache] Error saving cache:', error);
    }
  },

  /**
   * Add a notification to cache
   * @param {Object} notification - Notification to add
   */
  addNotification(notification) {
    const notifications = this.getNotifications();
    notifications.unshift(notification);
    this.saveNotifications(notifications);
  },

  /**
   * Clear notification cache
   */
  clearCache() {
    try {
      storage.remove(NOTIFICATIONS_CACHE_KEY);
    } catch (error) {
      console.error('[NotificationCache] Error clearing cache:', error);
    }
  },

  /**
   * Update a notification in cache
   * @param {string} id - Notification ID
   * @param {Object} updates - Fields to update
   */
  updateNotification(id, updates) {
    const notifications = this.getNotifications();
    const index = notifications.findIndex((n) => n.id === id);
    if (index !== -1) {
      notifications[index] = { ...notifications[index], ...updates };
      this.saveNotifications(notifications);
    }
  },

  /**
   * Remove a notification from cache
   * @param {string} id - Notification ID
   */
  removeNotification(id) {
    const notifications = this.getNotifications();
    const filtered = notifications.filter((n) => n.id !== id);
    this.saveNotifications(filtered);
  },
};
